/* Theme custom file */
/**
 * Sale gate
 *
 * Keeps a product's add-to-cart buttons locked while its sale is not open. The
 * sale-gate snippet renders the sale windows as "start-end" epoch seconds and
 * the server-side `closed` state, which stays authoritative: the visitor's clock
 * can only close the gate (a page left open past the end), never open it. The
 * buttons are disabled again whenever the product form re-enables them (variant
 * change), and the page reloads at the next sale edge so the server decides.
 */

/**
 * Same rule as snippets/sale-gate.liquid, on the visitor's clock: closed when
 * none of the product's sales is open.
 * @param {HTMLElement} gate - A <sale-gate> element, live or parsed
 * @returns {boolean}
 */
export function isSaleClosed(gate) {
  const now = Date.now() / 1000;
  const windows = (gate.dataset.windows ?? '').split(' ').map((pair) => pair.split('-').map(Number));
  return !windows.some(([start = 0, end = 0]) => start <= now && now < end);
}

class SaleGate extends HTMLElement {
  /** @type {ParentNode} */
  #root = document;
  #observer = new MutationObserver(() => this.#lock());
  #timer = 0;

  connectedCallback() {
    this.#root = this.closest('dialog, .shopify-section') ?? document;
    if (isSaleClosed(this)) this.setAttribute('closed', '');
    this.#lock();
    this.#observer.observe(this.#root, { subtree: true, childList: true, attributeFilter: ['disabled'] });

    const now = Date.now() / 1000;
    const edges = (this.dataset.windows ?? '').split(/[ -]/).map(Number).filter((edge) => edge > now);
    const delay = (Math.min(...edges) - now) * 1000;
    if (delay < 2 ** 31) this.#timer = window.setTimeout(() => location.reload(), delay);
  }

  disconnectedCallback() {
    this.#observer.disconnect();
    clearTimeout(this.#timer);
  }

  #lock() {
    if (!this.hasAttribute('closed')) return;
    for (const button of this.#root.querySelectorAll('[ref="addToCartButton"]')) {
      if (button instanceof HTMLButtonElement && !button.disabled) button.disabled = true;
    }
  }
}

if (!customElements.get('sale-gate')) {
  customElements.define('sale-gate', SaleGate);
}
